import { AlertBanner } from '@/components/common/AlertBanner'
import { useEpicSessionExpiry } from '@/features/auth/useEpicSessionExpiry'

/**
 * Warning banner shown ~5 minutes before an Epic (SMART on FHIR) launched
 * session is locked out, prompting the clinician to save their in-progress work
 * before the session expires. Renders nothing outside the warning window or for
 * standalone (non-Epic) sessions.
 *
 * The banner is intentionally non-dismissable: the risk (unsaved work lost at
 * lockout) persists until the session ends, so we keep it visible until the
 * lockout screen takes over.
 */
export function EpicSessionExpiryWarning({ className = '' }: { className?: string }) {
  const { isExpiryWarning, lockoutAt } = useEpicSessionExpiry()

  if (!isExpiryWarning) {
    return null
  }

  // Show the estimated timeout clock time (stable) rather than a live countdown:
  // the hook only re-renders when the warning/lockout boundaries change, so a
  // relative "in N minutes" value would freeze at its first-computed value.
  const timephrase =
    typeof lockoutAt === 'number'
      ? `at about ${new Intl.DateTimeFormat(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        }).format(new Date(lockoutAt))}`
      : 'in a few minutes'

  return (
    <AlertBanner variant="error" className={className}>
      {`Your Epic session will expire ${timephrase}. Please save or submit your in-progress work now — when the session expires you'll need to relaunch from Epic and unsaved changes may be lost.`}
    </AlertBanner>
  )
}

export default EpicSessionExpiryWarning
